/**
 * <ActivityGenerationForm> — le cadrage d'une activité à générer (organism).
 *
 * Les champs de `tutor/students/components/MaterialGenerationForm.js`, dans l'ordre du legacy :
 * Classe · Matière · Chapitre · Compétences travaillées · Type · Durée · Instructions. Mêmes
 * libellés, mêmes textes d'aide, dans la voix du lecteur (`voice`) : le parent est vouvoyé, l'élève
 * garde le tutoiement du legacy.
 *
 * Deux branches du legacy ne sont PAS ici, parce que ni le parent ni l'élève ne les atteignent : le
 * « Mode programme de révision » (l'interrupteur ne se rend que si `has_study_program` est vrai, et
 * la ressource que lisent le parent et l'élève ne porte pas ce champ) et le renvoi vers
 * l'« Ancienne interface » (réservé au tuteur).
 *
 * L'élève qui se génère une activité compose avec ce même formulaire. Ce que son parcours texte a
 * dans le legacy et que le parent n'a pas ici s'ajoute par prop, absent par défaut : les chapitres
 * de son prochain contrôle en tête de liste (`nextAssessment`), le chapitre OU les instructions
 * pour avancer (`chapterOrInstructions`), et la recherche sans résultat qui bascule vers
 * Instructions (`onDescribeInInstructions`, puis `instructionsRecommended`).
 *
 * Le tuteur, lui, atteint le « Mode programme de révision » (`studyProgram`, absent par défaut) :
 * l'interrupteur en tête du formulaire et, allumé, « Chapitre du programme » à la place du champ
 * Chapitre — le chapitre choisi dans le programme de l'élève, ou « Sélectionner un chapitre ».
 *
 * Compact (`compact`) : chaque choix est un champ d'autocomplétion qui montre sa valeur, son icône devant, et ouvre
 * sa liste sous lui, dans la page (`<SelectField presentation="inline">`), comme les sélecteurs de l'ancienne
 * fenêtre — Classe et Matière sur une ligne, le Chapitre qu'on cherche en tapant (le prochain contrôle en tête,
 * « Décrire « … » dans Instructions » quand rien ne vient), Type et Durée sur une ligne. Pas de feuille : au live, le
 * formulaire est déjà dans « Plus d'options ». Les icônes sont celles de l'ancienne fenêtre : le picto de la
 * matière, la jauge de difficulté du type, une horloge devant la durée. Le formulaire tient en un
 * écran et laisse voir les instructions et le dépôt d'un fichier. Défaut `false` : les rangées de
 * chips et la liste des chapitres dépliée, pour un réglage d'un seul champ (`<TutorSessionSettingSheet>`),
 * où toucher l'option tout de suite vaut mieux qu'un champ de plus à ouvrir.
 *
 * Contrôlé : `value` / `onChange` sur un seul brouillon, catalogues injectés. Le formulaire ne
 * décide de rien — pas même de sa validité, qui vient de l'écran.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { daysUntil, formatDaysUntil } from "../../../ports/primary/ui/formatters/dateFormatter"
import {
    type UIActivityGenerationCatalogs,
    type UIActivityGenerationDraft,
    type UIActivityGenerationField,
    type UIActivityGenerationMissingField,
    type UIActivityGenerationOption,
    type UISchoolAssessment,
    type UISectionedChapter,
    type UISubject,
} from "../../../ports/primary/ui/types"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { CHOICE_CHIP_HEIGHT } from "../../atoms/ChoiceChip/ChoiceChip"
import { DelayedSkeleton, Skeleton } from "../../atoms/Skeleton"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { Switch } from "../../atoms/Switch"
import { TextInput } from "../../atoms/TextInput"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { ErrorText } from "../../atoms/typography/ErrorText"
import { Text } from "../../atoms/typography/Text"
import { Autocomplete } from "../../molecules/Autocomplete"
import { type AutocompleteGroup, type AutocompleteItemIcon } from "../../molecules/Autocomplete/Autocomplete"
import { Card } from "../../molecules/Card"
import { HorizontalSelectInput } from "../../molecules/HorizontalSelectInput"
import { SelectField } from "../../molecules/SelectField"
import { URGENCY_VARIANT } from "../../molecules/SchoolAssessmentCard"
import { type Voice } from "../../utils/voice"
import intensityLevel1 from "../../../../assets/icons/intensity-level/intensity_level_1.png"
import intensityLevel2 from "../../../../assets/icons/intensity-level/intensity_level_2.png"
import intensityLevel3 from "../../../../assets/icons/intensity-level/intensity_level_3.png"

/** Le minimum d'instructions que le legacy accepte quand il n'y a pas de chapitre. */
const MIN_INSTRUCTIONS = 5

const SKILL_PARTS_LABEL = "Compétences travaillées"

/** Les compétences qui arrivent : des chips pleines, aux longueurs de noms de compétence. */
const SKILL_PART_BONE_WIDTHS = [150, 190, 130]

/**
 * Les mots adressés au lecteur. Ceux de l'élève sont ceux du legacy (`MaterialGenerationForm.js`,
 * `ChapterSearchDropdown.js`) ; le parent garde les siens.
 */
const TEXTS: Record<
    Voice,
    {
        chapterHint: string
        chapterOrInstructionsHint: string
        instructionsPlaceholder: string
        personalize: string
        describeHint: string
        classMissing: string
        subjectMissing: string
        instructionsMissing: string
    }
> = {
    parent: {
        chapterHint: "Choisissez un chapitre pour continuer",
        chapterOrInstructionsHint: "Choisissez un chapitre, ou décrivez l'exercice dans Instructions ci-dessous",
        instructionsPlaceholder: "Décrivez l'activité que vous souhaitez générer...",
        personalize: "Personnaliser avec ses centres d'intérêt",
        describeHint: "Aucun chapitre trouvé : Sam le créera à partir de votre description",
        classMissing: "Choisissez la classe pour continuer",
        subjectMissing: "Choisissez une matière pour continuer",
        instructionsMissing: `Décrivez l'activité pour continuer (${MIN_INSTRUCTIONS} caractères minimum)`,
    },
    student: {
        chapterHint: "Choisis un chapitre pour continuer",
        chapterOrInstructionsHint: "Choisis un chapitre, ou décris l'exercice dans Instructions ci-dessous",
        instructionsPlaceholder: "Décris l'activité que tu souhaites générer...",
        personalize: "Personnaliser avec tes centres d'intérêt",
        describeHint: "Aucun chapitre trouvé : Sam le créera à partir de ta description",
        classMissing: "Choisis ta classe pour continuer",
        subjectMissing: "Choisis une matière pour continuer",
        instructionsMissing: `Décris l'activité pour continuer (${MIN_INSTRUCTIONS} caractères minimum)`,
    },
}

/** Les mots qui ne s'adressent à personne : les mêmes dans les deux voix. */
const NEXT_ASSESSMENT_LABEL = "Prochain contrôle"
const NO_CHAPTER_LABEL = "Aucun chapitre disponible"
const SAM_CREATES_CHAPTER_LABEL = "Sam créera le chapitre"
const CHAPTERS_FAILED_LABEL = "La liste des chapitres n'a pas pu se charger."

/** Le mode programme de révision du tuteur — les mots du legacy. */
const STUDY_PROGRAM_LABEL = "Mode programme de révision"
const PROGRAM_CHAPTER_LABEL = "Chapitre du programme"
const PICK_PROGRAM_CHAPTER_LABEL = "Sélectionner un chapitre"

/** Le groupe qui remonte en tête de la liste des chapitres. */
const NEXT_ASSESSMENT_GROUP_ID = "next-assessment"

/** La jauge de difficulté de chaque type, celle de l'ancienne fenêtre (`materialLevelsIcons`). */
const LEVEL_ICONS: Record<string, number> = { "1": intensityLevel1, "2": intensityLevel2, "3": intensityLevel3 }

const levelIcon = (level: UIActivityGenerationOption): AutocompleteItemIcon | null => {
    if (level.iconUri != null) return { uri: level.iconUri }
    const source = LEVEL_ICONS[level.value]
    return source != null ? { source } : null
}
const subjectIcon = (subject: UISubject): AutocompleteItemIcon | null =>
    subject.iconUrl != null ? { uri: subject.iconUrl } : null
const durationIcon = (): AutocompleteItemIcon => "clock"

/** Tout le formulaire, dans l'ordre du legacy. */
const ALL_FIELDS: UIActivityGenerationField[] = ["scope", "chapter", "skillParts", "level", "duration", "instructions"]

export type ActivityGenerationFormProps = {
    value: UIActivityGenerationDraft
    onChange: (next: UIActivityGenerationDraft) => void
    catalogs: UIActivityGenerationCatalogs
    /** Le catalogue des chapitres est en train d'arriver. */
    chaptersLoading?: boolean
    /** Le catalogue des chapitres n'a pas pu se charger : le champ le dit, et `onRetryChapters` le relance. */
    chaptersFailed?: boolean
    onRetryChapters?: () => void
    /** Les compétences du chapitre choisi arrivent : la rangée en montre la forme. */
    skillPartsLoading?: boolean
    /**
     * Les champs à montrer, dans l'ordre du legacy quel que soit l'ordre donné. Par défaut, tous —
     * chaque étape du geste en demande une partie.
     */
    fields?: UIActivityGenerationField[]
    /**
     * Le chapitre est exigé. Faux quand la classe et la matière n'ont aucun chapitre validé :
     * le backend en crée un à partir de ce que l'analyse a lu, et ce sont les instructions qui
     * deviennent obligatoires.
     */
    chapterRequired?: boolean
    /** Ce qui s'intercale entre le chapitre et les compétences — le retour de l'analyse. */
    analysisSlot?: React.ReactNode
    /** Les centres d'intérêt de l'enfant, pour la chip « Personnaliser ». Absents → pas de chip. */
    studentInterests?: string | null
    /** À qui le formulaire parle. Défaut `"parent"`. */
    voice?: Voice
    /**
     * Le prochain contrôle de la matière choisie : ses chapitres remontent en tête de la liste,
     * sous « Prochain contrôle » et son échéance (« J-5 »). Absent → la liste du catalogue.
     */
    nextAssessment?: UISchoolAssessment | null
    /**
     * Les instructions tiennent lieu de chapitre (la règle du parcours texte : un chapitre OU cinq
     * caractères d'instructions). L'aide nomme alors les deux chemins et s'efface dès que l'un est
     * rempli, et un catalogue vide se dit (« Aucun chapitre disponible »). Défaut `false` : seul
     * le chapitre fait avancer (`chapterRequired`).
     */
    chapterOrInstructions?: boolean
    /**
     * La recherche d'un chapitre n'a rien trouvé, et le lecteur choisit de le décrire : l'écran
     * reçoit le texte cherché, le recopie dans Instructions (jamais par-dessus ce qui y est déjà)
     * et passe `instructionsRecommended`. Absent → la liste n'offre pas cette issue.
     */
    onDescribeInInstructions?: (searchText: string) => void
    /** Instructions est le chemin pris après une recherche sans résultat : « Sam créera le chapitre ». */
    instructionsRecommended?: boolean
    /**
     * Le « Mode programme de révision » du tuteur (`MaterialGenerationForm.js`, quand l'élève a un
     * programme). Absent (défaut) : ni l'interrupteur ni le chapitre du programme, le formulaire
     * d'avant au pixel.
     */
    studyProgram?: ActivityGenerationStudyProgram
    /**
     * Le champ qui manque, après un toucher sur « Générer » : il dit en rouge ce qu'il faut choisir.
     * Absent → aucun champ n'est en défaut.
     */
    fieldToFix?: UIActivityGenerationMissingField | null
    /** Reçoit la vue de chaque champ qui peut manquer, pour que l'écran l'amène à l'écran. */
    fieldRef?: (field: UIActivityGenerationMissingField, node: View | null) => void
    /**
     * Chaque choix en champ qui ouvre sa liste, deux par ligne : le formulaire tient en un écran.
     * Défaut `false` : les rangées de chips et la liste des chapitres dépliée.
     */
    compact?: boolean
    disabled?: boolean
    testID?: string
}

/** Le mode programme de révision : allumé ou non, et le chapitre choisi dans le programme. */
export type ActivityGenerationStudyProgram = {
    enabled: boolean
    onToggle: (next: boolean) => void
    /** Le chapitre du programme retenu — son nom et, s'il en a un, son chapitre parent. */
    chapter: { name: string; parentName?: string } | null
    /** Ouvre le choix d'un chapitre dans le programme de l'élève. */
    onPickChapter: () => void
}

export function ActivityGenerationForm(props: ActivityGenerationFormProps) {
    const {
        value,
        onChange,
        catalogs,
        chaptersLoading = false,
        chaptersFailed = false,
        onRetryChapters,
        skillPartsLoading = false,
        fields = ALL_FIELDS,
        chapterRequired = true,
        analysisSlot,
        studentInterests,
        voice = "parent",
        nextAssessment,
        chapterOrInstructions = false,
        onDescribeInInstructions,
        instructionsRecommended = false,
        studyProgram,
        fieldToFix = null,
        fieldRef,
        compact = false,
        disabled = false,
        testID,
    } = props
    // Le mode programme : présent et allumé, ou rien — la seule lecture de `studyProgram` qui suit.
    const program = studyProgram?.enabled === true ? studyProgram : null

    const texts = TEXTS[voice]
    const patch = (partial: Partial<UIActivityGenerationDraft>) => onChange({ ...value, ...partial })

    const shows = (field: UIActivityGenerationField) => fields.includes(field)
    const scopeReady = value.className != null && value.subjectId != null
    const skillParts = catalogs.skillParts ?? []
    const instructionsEnough = value.instructions.length >= MIN_INSTRUCTIONS
    const chapterHint = chapterOrInstructions ? texts.chapterOrInstructionsHint : texts.chapterHint
    // En défaut, l'aide passe en rouge sous le libellé : on la lit en arrivant sur le champ, pas
    // sous la liste des chapitres.
    const showsChapterHint =
        fieldToFix !== "chapter" &&
        (chapterOrInstructions
            ? value.chapterId == null && scopeReady && !instructionsEnough
            : chapterRequired && value.chapterId == null && scopeReady)
    const showsInstructionsHint =
        fieldToFix !== "instructions" && value.chapterId == null && value.instructions.length > 0 && !instructionsEnough
    const anchor = (field: UIActivityGenerationMissingField) =>
        fieldRef != null ? (node: View | null) => fieldRef(field, node) : undefined
    const showsRecommendedTag = instructionsRecommended && value.chapterId == null

    // Les chapitres du prochain contrôle passent devant, dans l'ordre du catalogue.
    const assessmentChapterIds = new Set((nextAssessment?.chaptersProgress ?? []).map((cp) => String(cp.chapter.id)))
    const inNextAssessment = (chapter: UISectionedChapter) => assessmentChapterIds.has(String(chapter.id))
    const chapters =
        assessmentChapterIds.size > 0
            ? [...catalogs.chapters.filter(inNextAssessment), ...catalogs.chapters.filter((c) => !inNextAssessment(c))]
            : catalogs.chapters
    const assessmentDays = nextAssessment != null ? daysUntil(nextAssessment.date) : 0
    const groupOf = (chapter: UISectionedChapter): AutocompleteGroup | null => {
        if (nextAssessment != null && inNextAssessment(chapter)) {
            return {
                id: NEXT_ASSESSMENT_GROUP_ID,
                label: NEXT_ASSESSMENT_LABEL,
                badge: {
                    text: formatDaysUntil(assessmentDays),
                    variant: URGENCY_VARIANT[nextAssessment.urgency],
                },
            }
        }
        return chapter.parent != null ? { id: chapter.parent.id, label: chapter.parent.name } : null
    }

    const chapterSearch = "Rechercher un chapitre..."
    const chapterPlaceholder = scopeReady ? chapterSearch : "Sélectionner une classe et matière d'abord"
    const selectedChapterIds = value.chapterId != null ? [value.chapterId] : []
    const noResultAction =
        onDescribeInInstructions != null
            ? {
                  label: (query: string) => `Décrire « ${query} » dans Instructions`,
                  hint: texts.describeHint,
                  onPress: onDescribeInInstructions,
              }
            : undefined
    // Un seul chapitre à la fois : re-choisir celui qui est choisi le retire.
    const toggleChapter = (id: string | number) =>
        patch({ chapterId: value.chapterId === id ? null : id, skillPartPositions: [] })
    const chapterError = fieldToFix === "chapter" ? chapterHint : undefined

    const showsNoChapter =
        chapterOrInstructions && scopeReady && !chaptersLoading && !chaptersFailed && catalogs.chapters.length === 0

    return (
        <Stack gap={compact ? "4" : "6"} testID={testID}>
            {studyProgram != null && (
                <Switch
                    checked={studyProgram.enabled}
                    onChange={studyProgram.onToggle}
                    label={STUDY_PROGRAM_LABEL}
                    disabled={disabled}
                />
            )}

            {compact && shows("scope") && (
                <Row gap="3" align="start">
                    <View ref={anchor("class")} style={styles.cell}>
                        <SelectField
                            presentation="inline"
                            searchable
                            label="Classe"
                            searchPlaceholder="Rechercher une classe..."
                            error={fieldToFix === "class" ? texts.classMissing : undefined}
                            items={catalogs.classes}
                            getItemId={(c) => c.value}
                            getItemLabel={(c) => c.label}
                            selectedIds={value.className != null ? [value.className] : []}
                            onChange={([id]) => {
                                if (id != null && String(id) !== value.className) {
                                    patch({ className: String(id), chapterId: null })
                                }
                            }}
                            disabled={disabled}
                        />
                    </View>
                    <View ref={anchor("subject")} style={styles.cell}>
                        <SelectField
                            presentation="inline"
                            searchable
                            label="Matière"
                            searchPlaceholder="Rechercher une matière..."
                            error={fieldToFix === "subject" ? texts.subjectMissing : undefined}
                            items={catalogs.subjects}
                            getItemId={(s) => s.id}
                            getItemLabel={(s) => s.name}
                            getItemIcon={subjectIcon}
                            selectedIds={value.subjectId != null ? [value.subjectId] : []}
                            onChange={([id]) => {
                                if (id != null && id !== value.subjectId) patch({ subjectId: id, chapterId: null })
                            }}
                            disabled={disabled}
                        />
                    </View>
                </Row>
            )}

            {!compact && shows("scope") && (
                <>
                    <View ref={anchor("class")}>
                        <HorizontalSelectInput
                            label="Classe"
                            error={fieldToFix === "class" ? texts.classMissing : undefined}
                            items={catalogs.classes}
                            getItemId={(c) => c.value}
                            renderItem={(option) => (
                                <ChoiceChip
                                    label={option.label}
                                    selected={option.value === value.className}
                                    disabled={disabled}
                                    onPress={() => patch({ className: option.value, chapterId: null })}
                                />
                            )}
                        />
                    </View>
                    <View ref={anchor("subject")}>
                        <HorizontalSelectInput
                            label="Matière"
                            error={fieldToFix === "subject" ? texts.subjectMissing : undefined}
                            items={catalogs.subjects}
                            getItemId={(s) => s.id}
                            renderItem={(subject) => (
                                <SubjectBadge
                                    subject={subject}
                                    size="md"
                                    showLabel
                                    selected={subject.id === value.subjectId}
                                    disabled={disabled}
                                    onSelect={() => patch({ subjectId: subject.id, chapterId: null })}
                                />
                            )}
                        />
                    </View>
                </>
            )}

            {program != null && shows("chapter") && (
                <View ref={anchor("chapter")}>
                    <Stack gap="3">
                        <Text size="text-base" weight="bold" color="primary">
                            {PROGRAM_CHAPTER_LABEL}
                        </Text>
                        {program.chapter != null ? (
                            <Card
                                pressable
                                onPress={program.onPickChapter}
                                disabled={disabled}
                                elevation="none"
                                p="3"
                                style={styles.programChapter}
                            >
                                <Row gap="2" align="center">
                                    <Stack gap="0" style={styles.programChapterBody}>
                                        {program.chapter.parentName != null && (
                                            <Text size="text-sm" color="text-muted">
                                                {program.chapter.parentName}
                                            </Text>
                                        )}
                                        <Text size="text-md" weight="semibold">
                                            {program.chapter.name}
                                        </Text>
                                    </Stack>
                                    <Icon name="chevron-right" size={16} color="text-muted" />
                                </Row>
                            </Card>
                        ) : (
                            <Button
                                label={PICK_PROGRAM_CHAPTER_LABEL}
                                iconName="plus"
                                fullWidth
                                disabled={disabled}
                                onPress={program.onPickChapter}
                            />
                        )}
                        {fieldToFix === "chapter" && <ErrorText live>{chapterHint}</ErrorText>}
                    </Stack>
                </View>
            )}

            {program == null && shows("chapter") && (
                <View ref={anchor("chapter")}>
                    <Stack gap="2">
                        {compact ? (
                            <SelectField
                                presentation="inline"
                                label="Chapitre"
                                placeholder={chapterPlaceholder}
                                error={chapterError}
                                items={chapters}
                                getItemId={(c) => c.id}
                                getItemLabel={(c) => c.name}
                                selectedIds={selectedChapterIds}
                                onChange={([id]) => id != null && toggleChapter(id)}
                                searchable
                                searchPlaceholder={chapterSearch}
                                groupBy={groupOf}
                                noResultAction={noResultAction}
                                loading={chaptersLoading}
                                disabled={disabled || !scopeReady}
                            />
                        ) : (
                            <Autocomplete
                                label="Chapitre"
                                error={chapterError}
                                items={chapters}
                                getItemId={(c) => c.id}
                                getItemLabel={(c) => c.name}
                                selectedIds={selectedChapterIds}
                                onToggle={toggleChapter}
                                groupBy={groupOf}
                                noResultAction={noResultAction}
                                loading={chaptersLoading}
                                disabled={disabled || !scopeReady}
                                searchPlaceholder={chapterPlaceholder}
                            />
                        )}
                        {chaptersFailed && !chaptersLoading && (
                            <Row gap="2" align="center" style={styles.wrap}>
                                <ErrorText live>{CHAPTERS_FAILED_LABEL}</ErrorText>
                                {onRetryChapters != null && (
                                    <TextButton
                                        label="Réessayer"
                                        size="sm"
                                        onPress={onRetryChapters}
                                        disabled={disabled}
                                    />
                                )}
                            </Row>
                        )}
                        {showsNoChapter && (
                            <Text size="text-sm" color="text-muted">
                                {NO_CHAPTER_LABEL}
                            </Text>
                        )}
                        {showsChapterHint && (
                            <Text size="text-sm" color="primary">
                                {chapterHint}
                            </Text>
                        )}
                    </Stack>
                </View>
            )}

            {analysisSlot}

            {shows("skillParts") && skillParts.length === 0 && skillPartsLoading && (
                <DelayedSkeleton accessibilityLabel="Chargement des compétences">
                    <Stack gap="3">
                        <Text size="text-base" weight="bold" color="primary">
                            {SKILL_PARTS_LABEL}
                        </Text>
                        <Row gap="2">
                            {SKILL_PART_BONE_WIDTHS.map((width) => (
                                <Skeleton
                                    key={width}
                                    shape="block"
                                    width={width}
                                    height={CHOICE_CHIP_HEIGHT}
                                    radius="full"
                                />
                            ))}
                        </Row>
                    </Stack>
                </DelayedSkeleton>
            )}

            {shows("skillParts") && skillParts.length > 0 && (
                <HorizontalSelectInput
                    label={SKILL_PARTS_LABEL}
                    items={skillParts}
                    getItemId={(sp) => sp.id}
                    renderItem={(skillPart) => (
                        <ChoiceChip
                            label={skillPart.name}
                            maxWidth={220}
                            selected={value.skillPartPositions.includes(skillPart.position)}
                            disabled={disabled}
                            onPress={() =>
                                patch({
                                    skillPartPositions: value.skillPartPositions.includes(skillPart.position)
                                        ? value.skillPartPositions.filter((p) => p !== skillPart.position)
                                        : [...value.skillPartPositions, skillPart.position],
                                })
                            }
                        />
                    )}
                />
            )}

            {compact && (shows("level") || shows("duration")) && (
                <Row gap="3" align="start">
                    {shows("level") && (
                        <View style={styles.cell}>
                            <SelectField
                                presentation="inline"
                                label="Type"
                                items={catalogs.levels}
                                getItemId={(l) => l.value}
                                getItemLabel={(l) => l.label}
                                getItemIcon={levelIcon}
                                selectedIds={[value.level]}
                                onChange={([id]) => id != null && patch({ level: String(id) })}
                                disabled={disabled}
                            />
                        </View>
                    )}
                    {shows("duration") && (
                        <View style={styles.cell}>
                            <SelectField
                                presentation="inline"
                                label="Durée"
                                items={catalogs.durations}
                                getItemId={(d) => d.value}
                                getItemLabel={(d) => d.label}
                                getItemIcon={durationIcon}
                                selectedIds={[value.duration]}
                                onChange={([id]) => id != null && patch({ duration: String(id) })}
                                disabled={disabled}
                            />
                        </View>
                    )}
                </Row>
            )}

            {!compact && shows("level") && (
                <HorizontalSelectInput
                    label="Type"
                    items={catalogs.levels}
                    getItemId={(l) => l.value}
                    renderItem={(option) => (
                        <ChoiceChip
                            label={option.label}
                            selected={option.value === value.level}
                            disabled={disabled}
                            onPress={() => patch({ level: option.value })}
                        />
                    )}
                />
            )}

            {!compact && shows("duration") && (
                <HorizontalSelectInput
                    label="Durée"
                    items={catalogs.durations}
                    getItemId={(d) => d.value}
                    renderItem={(option) => (
                        <ChoiceChip
                            label={option.label}
                            selected={option.value === value.duration}
                            disabled={disabled}
                            onPress={() => patch({ duration: option.value })}
                        />
                    )}
                />
            )}

            {shows("instructions") && (
                <View ref={anchor("instructions")}>
                    <Stack gap="2">
                        <TextInput
                            label="Instructions"
                            error={fieldToFix === "instructions" ? texts.instructionsMissing : undefined}
                            labelAccessory={
                                showsRecommendedTag ? (
                                    <Badge
                                        text={SAM_CREATES_CHAPTER_LABEL}
                                        variant="info"
                                        tinted
                                        iconName="magic"
                                        truncate={false}
                                    />
                                ) : undefined
                            }
                            value={value.instructions}
                            onChangeText={(instructions) => patch({ instructions })}
                            placeholder={texts.instructionsPlaceholder}
                            multiline
                            numberOfLines={3}
                            disabled={disabled}
                        />
                        {showsInstructionsHint && (
                            <Text size="text-sm" color="text-muted">
                                {`${MIN_INSTRUCTIONS} caractères minimum`}
                            </Text>
                        )}
                        {studentInterests != null && studentInterests !== "" && (
                            // Une chip se dimensionne sur son texte : dans une pile elle s'étirerait.
                            <Row>
                                <ChoiceChip
                                    label={value.personalizeWithInterests ? studentInterests : texts.personalize}
                                    selected={value.personalizeWithInterests}
                                    disabled={disabled}
                                    onPress={() => patch({ personalizeWithInterests: !value.personalizeWithInterests })}
                                />
                            </Row>
                        )}
                    </Stack>
                </View>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Le chapitre retenu se lit comme un champ : le contour du livre, pas une ombre. */
    programChapter: { borderWidth: 1, borderColor: theme.colors.border },
    programChapterBody: { flex: 1, minWidth: 0 },
    /** Deux champs côte à côte se partagent la ligne ; un nom long passe à la ligne dans le sien. */
    cell: { flex: 1, minWidth: 0 },
    wrap: { flexWrap: "wrap" },
}))
